import React, { useState, useEffect } from "react";
import {
  parseTimeTo24Hour,
  formatTimeForDisplay,
  generateGoogleCalendarUrl,
  calculateCountdown,
  generateSessionCalendarUrl,
} from "../../utils/eventUtils";
import { getResourceIcon } from "../../Components/icons/CommunityIcons";
import "./SessionDetailModal.css";

const IconClose = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconCalendar = (props) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconClock = (props) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconVideo = (props) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const IconExternalLink = (props) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const IconGoogleCalendar = (props) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
    <path d="M8 14l2 2 4-4" />
  </svg>
);

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/* Format short date for sessions list */
function formatShortDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export default function SessionDetailModal({ event, rsvpStatus, onRSVP, onClose, isLoading, allSessions = [] }) {
  const isAccepted = rsvpStatus === "accepted";
  const isPending = rsvpStatus === "pending";
  const hasRSVP = rsvpStatus !== null;

  const [countdown, setCountdown] = useState(() =>
    calculateCountdown(event?.nextEventDate, event?.startTime)
  );

  useEffect(() => {
    if (!isAccepted) return;

    const interval = setInterval(() => {
      setCountdown(calculateCountdown(event?.nextEventDate, event?.startTime));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [event?.nextEventDate, event?.startTime, isAccepted]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!event) return null;

  return (
    <div className="session-modal-overlay" onClick={handleOverlayClick}>
      <div className="session-modal">
        {/* Header */}
        <div className="session-modal-header">
          <div>
            <span className="session-cadence-badge">
              {event.cadence}
            </span>
            <h2>{event.title}</h2>
          </div>
          <button
            type="button"
            className="session-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>

        {/* Content */}
        <div className="session-modal-content">
          {event.description && (
            <p className="session-description">{event.description}</p>
          )}

          <div className="session-details">
            <div className="session-detail-row">
              <IconCalendar />
              <span>{formatDate(event.nextEventDate)}</span>
            </div>
            <div className="session-detail-row">
              <IconClock />
              <span>{formatTimeForDisplay(event.startTime)} - {formatTimeForDisplay(event.endTime)}</span>
            </div>
          </div>

          {/* Status Banner - shows for pending or accepted users */}
          {isPending && (
            <div className="session-status-banner pending">
              <span>Your RSVP is pending admin approval</span>
            </div>
          )}

          {isAccepted && countdown && !countdown.isLive && (
            <div className="session-status-banner countdown">
              <IconClock />
              <span>
                {countdown.days > 0 && `${countdown.days}d `}
                {countdown.hours}h {countdown.minutes}m until meeting
              </span>
            </div>
          )}

          {/* Action buttons - only show for accepted users */}
          {isAccepted && (
            <div className="session-action-buttons">
              {event.meetingLink && countdown?.isLive && (
                <a
                  href={event.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="session-join-btn"
                >
                  <IconVideo />
                  <span>Join Meeting</span>
                  <IconExternalLink />
                </a>
              )}
              <a
                href={generateGoogleCalendarUrl(event)}
                target="_blank"
                rel="noopener noreferrer"
                className="session-calendar-btn"
              >
                <IconGoogleCalendar />
                <span>Add to Calendar</span>
                <IconExternalLink />
              </a>
            </div>
          )}

          {/* Resources - only show for accepted users */}
          {isAccepted && event.resources && event.resources.length > 0 && (
            <div className="session-resources">
              <h3>Resources</h3>
              <div className="resources-list">
                {event.resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="resource-item"
                  >
                    {getResourceIcon(resource.type)}
                    <div className="resource-info">
                      <span className="resource-title">{resource.title}</span>
                      <span className="resource-type">{resource.type}</span>
                    </div>
                    <IconExternalLink className="resource-link-icon" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming Sessions List */}
          {allSessions && allSessions.length > 0 && (
            <div className="session-upcoming-list">
              <h3>Upcoming Sessions</h3>
              <div className="upcoming-sessions">
                {allSessions.map((session, index) => {
                  const sessionDate = session.instanceDate || session.nextEventDate;
                  return (
                    <div key={session._id || index} className="upcoming-session-item">
                      <div className="upcoming-session-info">
                        <span className="upcoming-session-date">
                          {formatShortDate(sessionDate)}
                        </span>
                        <span className="upcoming-session-time">
                          {formatTimeForDisplay(session.startTime)} - {formatTimeForDisplay(session.endTime)}
                        </span>
                      </div>
                      {isAccepted && (
                        <a
                          href={generateSessionCalendarUrl(session, event.title)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="upcoming-session-cal-btn"
                          title="Add to Calendar"
                        >
                          <IconGoogleCalendar width="16" height="16" />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="session-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {isAccepted ? (
            <button className="btn btn-accepted" disabled>
              RSVP Confirmed
            </button>
          ) : isPending ? (
            <button className="btn btn-pending" disabled>
              Pending Approval
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={onRSVP}
              disabled={isLoading}
            >
              {isLoading ? "Requesting..." : "RSVP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
